import { UploadedFile } from '../components/multi-file-upload/multi-file-upload.component';
import { FILE_EXTENSIONS } from '../constants/file-type.constants';
import { FileExtension } from '../enums/file-type.enum';

/**
 * Returns a File object if the provided input contains one, otherwise undefined.
 */
export function getFileForPreview(
  file: Partial<UploadedFile>
): File | undefined {
  return file?.file instanceof File ? file.file : undefined;
}

/**
 * Get the correct file name from either filename property or file.name
 */
export function getFileName(file: Partial<UploadedFile>): string {
  // Priority: filename property > file.name > fallback
  return file.filename || file.file?.name || 'Unknown file';
}

/**
 * Get the file extension from the filename
 */
export function getFileExtension(file: Partial<UploadedFile>): string {
  const fileName = getFileName(file);
  const lastDot = fileName.lastIndexOf('.');

  if (lastDot === -1 || lastDot === fileName.length - 1) {
    return 'FILE'; // No extension found
  }

  return fileName.substring(lastDot + 1).toUpperCase();
}

/**
 * Determines the MIME content type of a file.
 * Prefers explicit `contentType` or File object's `type`,
 * otherwise infers from file extension.
 */
export function getFileContentType(file: Partial<UploadedFile>): string {
  // 1️⃣ Use File object's native type (browser file uploads)
  if (file?.file instanceof File && file.file.type) {
    return file.file.type;
  }

  // 2️⃣ Use API's contentType field if present
  if (file?.contentType) {
    return file.contentType;
  }

  // 3️⃣ Use generic type property if it exists
  if (file?.type) {
    return file.type;
  }

  // 4️⃣ Infer from file extension as last resort
  const fileName = getFileName(file);
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    // ---------------- DOCUMENTS ----------------
    case FileExtension.PDF:
      return 'application/pdf';

    case FileExtension.DOC:
      return 'application/msword';
    case FileExtension.DOCX:
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    case FileExtension.XLS:
      return 'application/vnd.ms-excel';
    case FileExtension.XLSX:
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case FileExtension.ODS:
      return 'application/vnd.oasis.opendocument.spreadsheet';

    case FileExtension.PPT:
      return 'application/vnd.ms-powerpoint';
    case FileExtension.PPTX:
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case FileExtension.ODP:
      return 'application/vnd.oasis.opendocument.presentation';

    case FileExtension.ODT:
      return 'application/vnd.oasis.opendocument.text';

    case FileExtension.RTF:
      return 'application/rtf';
    case FileExtension.TXT:
      return 'text/plain';
    case FileExtension.CSV:
      return 'text/csv';
    case FileExtension.MD:
      return 'text/markdown';
    case FileExtension.LOG:
      return 'text/plain';

    // ---------------- IMAGES ----------------
    case FileExtension.JPG:
    case FileExtension.JPEG:
      return 'image/jpeg';

    case FileExtension.PNG:
      return 'image/png';
    case FileExtension.GIF:
      return 'image/gif';
    case FileExtension.BMP:
      return 'image/bmp';
    case FileExtension.SVG:
      return 'image/svg+xml';
    case FileExtension.WEBP:
      return 'image/webp';

    case FileExtension.TIF:
    case FileExtension.TIFF:
      return 'image/tiff';

    case FileExtension.ICO:
      return 'image/x-icon';
    case FileExtension.HEIC:
      return 'image/heic';
    case FileExtension.RAW:
      return 'image/x-raw';
    case FileExtension.PSD:
      return 'image/vnd.adobe.photoshop';

    // ---------------- VIDEOS ----------------
    case FileExtension.MP4:
      return 'video/mp4';
    case FileExtension.MOV:
      return 'video/quicktime';
    case FileExtension.AVI:
      return 'video/x-msvideo';
    case FileExtension.WMV:
      return 'video/x-ms-wmv';
    case FileExtension.FLV:
      return 'video/x-flv';
    case FileExtension.MKV:
      return 'video/x-matroska';
    case FileExtension.WEBM:
      return 'video/webm';
    case FileExtension.M4V:
      return 'video/x-m4v';

    case FileExtension.MPG:
    case FileExtension.MPEG:
      return 'video/mpeg';

    case FileExtension.GP3:
      return 'video/3gpp';

    // ---------------- AUDIO ----------------
    case FileExtension.MP3:
      return 'audio/mpeg';
    case FileExtension.WAV:
      return 'audio/wav';
    case FileExtension.AAC:
      return 'audio/aac';
    case FileExtension.OGG:
      return 'audio/ogg';
    case FileExtension.FLAC:
      return 'audio/flac';
    case FileExtension.M4A:
      return 'audio/m4a';
    case FileExtension.WMA:
      return 'audio/x-ms-wma';
    case FileExtension.AMR:
      return 'audio/amr';

    // ---------------- ARCHIVES ----------------
    case FileExtension.ZIP:
      return 'application/zip';
    case FileExtension.RAR:
      return 'application/vnd.rar';
    case FileExtension.TAR:
      return 'application/x-tar';
    case FileExtension.GZ:
      return 'application/gzip';
    case FileExtension._7Z:
      return 'application/x-7z-compressed';
    case FileExtension.ISO:
      return 'application/x-iso9660-image';
    case FileExtension.CAB:
      return 'application/vnd.ms-cab-compressed';

    // ---------------- CODE / MARKUP ----------------
    case FileExtension.HTML:
    case FileExtension.HTM:
      return 'text/html';
    case FileExtension.JS:
      return 'application/javascript';
    case FileExtension.JSON:
      return 'application/json';
    case FileExtension.XML:
      return 'application/xml';
    case FileExtension.YAML:
    case FileExtension.YML:
      return 'application/x-yaml';

    // ---------------- DESIGN / CAD ----------------
    case FileExtension.AI:
      return 'application/postscript';
    case FileExtension.EPS:
      return 'application/postscript';
    case FileExtension.INDD:
      return 'application/x-indesign';
    case FileExtension.DWG:
      return 'image/vnd.dwg';
    case FileExtension.DXF:
      return 'image/vnd.dxf';

    // ---------------- DEFAULT ----------------
    default:
      return 'application/octet-stream';
  }
}

/**
 * Utility class for detecting file types.
 * PRIORITY: contentType → extension fallback.
 */
export class FileUtil {
  static getExtension(fileUrl?: string): string {
    if (!fileUrl) {
      return '';
    }
    const cleanUrl = fileUrl.split('?')[0];
    return cleanUrl.split('.').pop()?.toLowerCase() || '';
  }

  static matches(list: string[], ext: string): boolean {
    return list.includes(ext);
  }

  // --------------------------------------------------------------
  // VIDEO
  // --------------------------------------------------------------
  static isVideoFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return ct.startsWith('video/') || this.matches(FILE_EXTENSIONS.video, ext);
  }

  // --------------------------------------------------------------
  // AUDIO
  // --------------------------------------------------------------
  static isAudioFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return ct.startsWith('audio/') || this.matches(FILE_EXTENSIONS.audio, ext);
  }

  // --------------------------------------------------------------
  // IMAGE
  // --------------------------------------------------------------
  static isImageFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return ct.startsWith('image/') || this.matches(FILE_EXTENSIONS.image, ext);
  }

  // --------------------------------------------------------------
  // PDF
  // --------------------------------------------------------------
  static isPdfFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return ct === 'application/pdf' || ext === 'pdf';
  }

  // --------------------------------------------------------------
  // EXCEL
  // --------------------------------------------------------------
  static isExcelFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return (
      ct === 'application/vnd.ms-excel' ||
      ct ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      ct === 'application/vnd.oasis.opendocument.spreadsheet' ||
      this.matches(FILE_EXTENSIONS.excel, ext)
    );
  }

  // --------------------------------------------------------------
  // CSV
  // --------------------------------------------------------------
  static isCsvFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return ct === 'text/csv' || ct === 'application/csv' || ext === 'csv';
  }

  // --------------------------------------------------------------
  // TEXT (EXCLUDING CODE)
  // --------------------------------------------------------------
  static isTextFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    if (this.isCodeFile(ct, fileUrl)) {
      return false;
    }

    return (
      ct === 'text/plain' ||
      ct === 'text/markdown' ||
      ct === 'application/rtf' ||
      this.matches(FILE_EXTENSIONS.text, ext)
    );
  }

  // --------------------------------------------------------------
  // WORD
  // --------------------------------------------------------------
  static isWordFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return (
      ct === 'application/msword' ||
      ct ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      ct === 'application/vnd.oasis.opendocument.text' ||
      this.matches(FILE_EXTENSIONS.word, ext)
    );
  }

  // --------------------------------------------------------------
  // POWERPOINT
  // --------------------------------------------------------------
  static isPowerPointFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    return (
      ct === 'application/vnd.ms-powerpoint' ||
      ct ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      ct === 'application/vnd.oasis.opendocument.presentation' ||
      ct.includes('presentation') ||
      ct.includes('powerpoint') ||
      this.matches(FILE_EXTENSIONS.ppt, ext)
    );
  }

  // --------------------------------------------------------------
  // ZIP / ARCHIVES
  // --------------------------------------------------------------
  static isZipFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    const archiveTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'application/vnd.rar',
      'application/x-tar',
      'application/gzip',
      'application/x-7z-compressed',
      'application/x-iso9660-image',
      'application/vnd.ms-cab-compressed',
    ];

    return (
      archiveTypes.includes(ct) ||
      ct.includes('zip') ||
      this.matches(FILE_EXTENSIONS.zip, ext)
    );
  }

  // --------------------------------------------------------------
  // CODE FILES
  // --------------------------------------------------------------
  static isCodeFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    const codeTypes = [
      'text/html',
      'application/json',
      'application/xml',
      'text/xml',
      'application/javascript',
      'text/javascript',
      'application/x-yaml',
      'text/yaml',
    ];

    return codeTypes.includes(ct) || this.matches(FILE_EXTENSIONS.code, ext);
  }

  // --------------------------------------------------------------
  // DESIGN / CAD FILES
  // --------------------------------------------------------------
  static isDesignFile(contentType: string, fileUrl?: string): boolean {
    const ct = contentType || '';
    const ext = this.getExtension(fileUrl);

    const cadTypes = [
      'application/postscript',
      'application/x-indesign',
      'image/vnd.dwg',
      'image/vnd.dxf',
    ];

    return cadTypes.includes(ct) || this.matches(FILE_EXTENSIONS.design, ext);
  }
}
